import { UnseenReviewBar } from "@/components/admin/UnseenReviewBar";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ExecutivePage } from "@/components/executive/system/ExecutivePage";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "@/hooks/use-toast";
import {
  Users, GraduationCap, Calendar, MapPin, Loader2, Sprout, Search, Plus,
  CheckCircle2, XCircle, RotateCcw, StickyNote, UserPlus, Bell, Send,
  ChevronRight, Copy,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2 } from "lucide-react";
import {
  fetchEvents, useOpenHouseSnapshot, useOpenHouseRegistrations,
  checkInRegistration, markNoShow, resetArrival, adjustHeadcount, addNote,
  completeFollowUp, markReviewed, defaultReminderSchedule,
  countEventRegistrations, deleteEvent,
  type OpenHouseEvent, type Registration, type Snapshot, type ReminderKind,
  formatEventDate,
} from "@/lib/openHouse";
import { adminStatusLabel, formatOpenHouseTime, pickFeaturedOpenHouse, todayInTimezone } from "@/lib/openHouseSchedule";
import { OpenHouseEventDialog } from "@/components/admin/openHouse/OpenHouseEventDialog";

export default function OpenHouseAdminPage() {
  const [events, setEvents] = useState<OpenHouseEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const modeParam = searchParams.get("mode");
  const eventParam = searchParams.get("event");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<OpenHouseEvent | null>(null);
  const [deleting, setDeleting] = useState<OpenHouseEvent | null>(null);

  const loadEvents = async () => {
    const list = await fetchEvents();
    setEvents(list);
    setLoading(false);
    return list;
  };
  useEffect(() => { loadEvents(); }, []);

  // Default selection: the open house the public page is featuring, else the most recent one.
  const defaultId = useMemo(() => {
    const featured = pickFeaturedOpenHouse(events, todayInTimezone());
    return featured?.id ?? events[0]?.id ?? null;
  }, [events]);
  const selectedId = eventParam && events.some((e) => e.id === eventParam) ? eventParam : defaultId;
  const selectEvent = (id: string) => setSearchParams({ event: id });

  const { snapshot: snapshotRaw, refresh: refreshSnap } = useOpenHouseSnapshot(selectedId);
  const { regs, refresh: refreshRegs } = useOpenHouseRegistrations(selectedId);
  const event = events.find((e) => e.id === selectedId) ?? null;
  // Never hide the event just because the aggregate snapshot RPC hasn't returned
  // (or errored for a non-admin viewer). Fall back to a zeroed snapshot derived
  // from the event so admin + public always show the same source of truth.
  const snapshot: Snapshot | null = snapshotRaw ?? (event ? {
    event,
    total_headcount: 0, parent_families: 0, parent_heads: 0, teacher_heads: 0,
    walk_ins: 0, checked_in: 0, checked_in_heads: 0, no_shows: 0, cancelled: 0,
    expected: 0, follow_ups_needed: 0,
    seats_remaining: event.capacity_total ?? null,
    capacity_total: event.capacity_total ?? null,
    new_since_last_review: 0, class_demand: [],
  } : null);
  const refresh = () => { refreshSnap(); refreshRegs(); };

  const openCreate = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (e: OpenHouseEvent) => { setEditing(e); setEditorOpen(true); };
  const onSaved = async (id: string) => {
    await loadEvents();
    selectEvent(id);
    refreshSnap();
  };

  if (modeParam === "check-in" && event && snapshot) {
    return (
      <ExecutivePage section="open_house">
        <CheckInMode
          event={event} regs={regs} snapshot={snapshot}
          onExit={() => setSearchParams({ event: event.id })} onChange={refresh}
        />
      </ExecutivePage>
    );
  }

  return (
    <ExecutivePage section="open_house">
      <UnseenReviewBar
        badge="open_house"
        note="Marking these read only clears the sidebar bubble — review status and follow-up actions are unchanged."
      />

      {!loading && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          {events.length > 0 ? (
            <div className="space-y-1 min-w-[16rem] flex-1 max-w-md">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Viewing open house</Label>
              <Select value={selectedId ?? undefined} onValueChange={selectEvent}>
                <SelectTrigger aria-label="Choose an open house"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {events.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {new Date(e.event_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      {" · "}{e.title} · {adminStatusLabel(e.status)}{e.public_visible ? "" : " · Unpublished"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : <div />}
          <Button onClick={openCreate} className="gap-2"><Plus className="h-4 w-4" /> Add Open House</Button>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : !event || !snapshot ? (
        <EmptyEvents onAdd={openCreate} />
      ) : (
        <>
          <EventHeader
            event={event}
            onEnterCheckIn={() => setSearchParams({ event: event.id, mode: "check-in" })}
            onEdit={() => openEdit(event)}
            onDelete={() => setDeleting(event)}
          />
          <ExecutiveSummary snapshot={snapshot} />
          <TodaysFocus snapshot={snapshot} regs={regs} onGoToReminders={() => document.getElementById("oh-tabs-reminders")?.click()} />

          <Tabs key={event.id} defaultValue="overview" className="pt-2">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="rsvps">RSVPs</TabsTrigger>
              <TabsTrigger value="attendance">Attendance & Follow-Up</TabsTrigger>
              <TabsTrigger value="reminders" id="oh-tabs-reminders">Reminders</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="pt-4 space-y-4">
              <ClassDemandCard snapshot={snapshot} />
              <NextStepsCard event={event} snapshot={snapshot} />
            </TabsContent>
            <TabsContent value="rsvps" className="pt-4">
              <RsvpList regs={regs} onChange={refresh} />
            </TabsContent>
            <TabsContent value="attendance" className="pt-4">
              <AttendanceAndFollowUp event={event} regs={regs} onChange={refresh} />
            </TabsContent>
            <TabsContent value="reminders" className="pt-4">
              <RemindersTab event={event} onChange={refresh} />
            </TabsContent>
          </Tabs>

          <Card>
            <CardHeader><CardTitle className="font-heading text-base">All open houses</CardTitle></CardHeader>
            <CardContent className="p-0 divide-y">
              {events.map((e) => (
                <div key={e.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                  <button onClick={() => selectEvent(e.id)} className="text-left min-w-0 flex-1 hover:text-primary">
                    <p className="text-sm font-medium">
                      {e.title}{e.id === selectedId && <span className="text-xs text-primary font-normal"> · viewing</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatEventDate(e.event_date)}{e.location_name ? ` · ${e.location_name}` : ""}
                    </p>
                  </button>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={e.status} />
                    {!e.public_visible && <Badge variant="outline">Unpublished</Badge>}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(e)} aria-label={`Edit ${e.title}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDeleting(e)} aria-label={`Delete ${e.title}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}

      <OpenHouseEventDialog open={editorOpen} onOpenChange={setEditorOpen} event={editing} onSaved={onSaved} />
      <DeleteEventDialog
        event={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={async (id) => {
          setDeleting(null);
          const list = await loadEvents();
          if (selectedId === id) setSearchParams(list[0] ? { event: list[0].id } : {});
        }}
      />
    </ExecutivePage>
  );
}

/* ───────────────────── UI Blocks ───────────────────── */

function DeleteEventDialog({ event, onClose, onDeleted }: {
  event: OpenHouseEvent | null; onClose: () => void; onDeleted: (id: string) => void;
}) {
  const [rsvpCount, setRsvpCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setRsvpCount(null);
    if (event) countEventRegistrations(event.id).then(setRsvpCount).catch(() => setRsvpCount(-1));
  }, [event]);

  const blocked = rsvpCount !== null && rsvpCount !== 0;
  const confirm = async () => {
    if (!event) return;
    setBusy(true);
    try {
      await deleteEvent(event.id);
      toast({ title: "Open House deleted" });
      onDeleted(event.id);
    } catch (err: any) {
      toast({ title: "Couldn't delete", description: err?.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={!!event} onOpenChange={(o) => { if (!o) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this Open House?</AlertDialogTitle>
          <AlertDialogDescription>
            {event && <>{event.title} · {formatEventDate(event.event_date)}<br /></>}
            {rsvpCount === null ? "Checking for RSVPs…"
              : rsvpCount === -1 ? "We couldn't check for RSVPs, so deleting is turned off for now."
              : rsvpCount > 0
                ? `This open house has ${rsvpCount} RSVP${rsvpCount === 1 ? "" : "s"}, so it can't be deleted. To take it down, edit it and set the status to Cancelled or turn Published off.`
                : "It has no RSVPs. This can't be undone."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          {!blocked && rsvpCount === 0 && (
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirm(); }}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Delete
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EmptyEvents({ onAdd }: { onAdd: () => void }) {
  return (
    <Card>
      <CardContent className="py-16 text-center space-y-3">
        <Sprout className="h-8 w-8 mx-auto text-primary" />
        <h1 className="font-heading text-2xl">No Open House scheduled yet</h1>
        <p className="text-muted-foreground max-w-md mx-auto">
          Add your first Open House date to start collecting RSVPs.
        </p>
        <Button onClick={onAdd} className="gap-2"><Plus className="h-4 w-4" /> Add Open House</Button>
      </CardContent>
    </Card>
  );
}

function EventHeader({ event, onEnterCheckIn, onEdit, onDelete }: {
  event: OpenHouseEvent; onEnterCheckIn: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const time = formatOpenHouseTime(event.start_time, event.end_time, event.end_time_approximate);
  return (
    <header className="flex items-start justify-between gap-4 flex-wrap">
      <div className="space-y-1">
        <p className="text-xs uppercase tracking-wide text-primary font-semibold">Open House</p>
        <h1 className="font-heading text-3xl">{event.title}</h1>
        <p className="text-sm text-muted-foreground">
          <Calendar className="inline h-3.5 w-3.5 mr-1" />
          {formatEventDate(event.event_date)}{time && <> · {time}</>}
          {event.location_name && <>  ·  <MapPin className="inline h-3.5 w-3.5 mr-1" />{event.location_name}</>}
        </p>
        <div className="pt-2 flex gap-2 items-center flex-wrap">
          <StatusBadge status={event.status} />
          {!event.public_visible && <Badge variant="outline">Hidden from public site</Badge>}
        </div>
        {(event.status === "postponed" || event.status === "cancelled") && event.status_note && (
          <p className="text-xs text-muted-foreground max-w-xl pt-1">Public announcement: {event.status_note}</p>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" onClick={onEdit} className="gap-2"><Pencil className="h-4 w-4" /> Edit</Button>
        <Button variant="outline" onClick={onDelete} className="gap-2 text-destructive"><Trash2 className="h-4 w-4" /> Delete</Button>
        <Button onClick={onEnterCheckIn} size="lg" className="gap-2">
          <CheckCircle2 className="h-4 w-4" /> Start check-in
        </Button>
      </div>
    </header>
  );
}

function ExecutiveSummary({ snapshot }: { snapshot: Snapshot }) {
  const s = snapshot;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat label="Expected attendance" value={s.total_headcount} highlight />
      <Stat label="Families RSVP'd" value={s.parent_families} sub={`${s.parent_heads} heads`} />
      <Stat label="Instructors" value={s.teacher_heads} />
      <Stat label="Seats remaining" value={s.seats_remaining ?? "—"} sub={s.capacity_total ? `of ${s.capacity_total}` : "no cap set"} />
      <Stat label="New since last review" value={s.new_since_last_review} accent />
      <Stat label="Follow-ups needed" value={s.follow_ups_needed} accent />
      <Stat label="Checked in" value={s.checked_in_heads} sub={`${s.checked_in} record${s.checked_in === 1 ? "" : "s"}`} />
      <Stat label="No-shows" value={s.no_shows} />
    </div>
  );
}

function TodaysFocus({ snapshot, regs, onGoToReminders }: { snapshot: Snapshot; regs: Registration[]; onGoToReminders: () => void }) {
  const focus: { title: string; sub: string; action?: () => void; actionLabel?: string }[] = [];
  const newCount = snapshot.new_since_last_review;
  if (newCount > 0) focus.push({ title: `Review ${newCount} new RSVP${newCount === 1 ? "" : "s"}`, sub: "Just came in since your last look" });
  if (snapshot.follow_ups_needed > 0) focus.push({
    title: `Follow up with ${snapshot.follow_ups_needed} attendee${snapshot.follow_ups_needed === 1 ? "" : "s"}`,
    sub: "Marked as needing a personal note",
  });
  const daysAway = Math.round((new Date(snapshot.event.event_date + "T12:00:00").getTime() - Date.now()) / 86400000);
  if (daysAway === 2 || daysAway === 7) focus.push({
    title: `Send the ${daysAway}-day reminder`, sub: "It's the right time", action: onGoToReminders, actionLabel: "Open reminders",
  });
  if (focus.length === 0) return null;
  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Today's focus</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {focus.slice(0, 3).map((f, i) => (
          <div key={i} className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-sm">{f.title}</p>
              <p className="text-xs text-muted-foreground">{f.sub}</p>
            </div>
            {f.action && <Button size="sm" variant="ghost" onClick={f.action}>{f.actionLabel}</Button>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ClassDemandCard({ snapshot }: { snapshot: Snapshot }) {
  const parent = snapshot.class_demand.filter((c) => c.interest_type === "parent_interest").slice(0, 8);
  const teachIdeas = snapshot.class_demand.filter((c) => c.interest_type === "interested_in_teaching");
  const currentTeaching = snapshot.class_demand.filter((c) => c.interest_type === "currently_teaches");
  if (parent.length + teachIdeas.length + currentTeaching.length === 0) return null;
  return (
    <Card>
      <CardHeader><CardTitle className="font-heading text-base">Class interest at a glance</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {parent.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Families want to learn about</p>
            <div className="space-y-1">
              {parent.map((c) => (
                <div key={c.title} className="flex items-center justify-between text-sm">
                  <span>{c.title}</span>
                  <Badge variant="outline">{c.demand}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        {teachIdeas.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Class ideas from prospective instructors</p>
            {teachIdeas.map((c) => (
              <p key={c.title} className="text-sm">• {c.title}</p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function NextStepsCard({ event, snapshot }: { event: OpenHouseEvent; snapshot: Snapshot }) {
  return (
    <Card>
      <CardHeader><CardTitle className="font-heading text-base">What MAVIS suggests</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-2">
        <p>Ask the assistant "How many people are coming to the Open House?" or "Send everyone a 2-day reminder."</p>
        <p className="text-muted-foreground">
          {snapshot.total_headcount} attendee{snapshot.total_headcount === 1 ? "" : "s"} across {snapshot.parent_families} famil{snapshot.parent_families === 1 ? "y" : "ies"}
          {" "}and {snapshot.teacher_heads} instructor{snapshot.teacher_heads === 1 ? "" : "s"}.
        </p>
      </CardContent>
    </Card>
  );
}

/* ───────────────────── RSVP List ───────────────────── */

function RsvpList({ regs, onChange }: { regs: Registration[]; onChange: () => void }) {
  const [tab, setTab] = useState<"parent" | "teacher">("parent");
  const active = regs.filter((r) => r.registration_status !== "cancelled" && r.arrival_status !== "cancelled");
  const filtered = active.filter((r) => r.attendee_type === tab);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="font-heading">RSVPs</CardTitle>
        <div className="flex gap-1">
          <PillButton active={tab === "parent"} onClick={() => setTab("parent")} icon={Users}
            label={`Parents (${active.filter(r => r.attendee_type === "parent").length})`} />
          <PillButton active={tab === "teacher"} onClick={() => setTab("teacher")} icon={GraduationCap}
            label={`Instructors (${active.filter(r => r.attendee_type === "teacher").length})`} />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {filtered.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">No {tab === "parent" ? "families" : "instructors"} yet.</p>
        ) : (
          <div className="divide-y">
            {filtered.map((r) => (
              <div key={r.id} className="px-4 py-3 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{r.first_name} {r.last_name}</span>
                    {r.is_walk_in && <Badge variant="outline" className="text-[10px]">Walk-in</Badge>}
                    {r.review_status === "new" && !r.is_walk_in && <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-[10px]">New</Badge>}
                    <ArrivalBadge status={r.arrival_status} />
                    {r.instructor_status && <span className="text-xs text-muted-foreground">· {r.instructor_status}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {r.email}{r.phone ? ` · ${r.phone}` : ""}
                  </div>
                  <div className="text-xs mt-1">
                    {r.total_headcount} attendee{r.total_headcount === 1 ? "" : "s"}
                    {r.attendee_type === "parent" && <> ({r.adult_count} adult{r.adult_count === 1 ? "" : "s"}, {r.child_count} child{r.child_count === 1 ? "" : "ren"})</>}
                  </div>
                  {r.questions && <p className="text-xs text-muted-foreground mt-2 italic">"{r.questions}"</p>}
                  {r.internal_notes && <p className="text-xs text-primary mt-2 whitespace-pre-line">{r.internal_notes}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-[11px] text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                  {r.review_status === "new" && (
                    <Button size="sm" variant="ghost" onClick={async () => { await markReviewed(r); onChange(); }}>Mark reviewed</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ───────────────────── Attendance & Follow-Up ───────────────────── */

function AttendanceAndFollowUp({ event, regs, onChange }: { event: OpenHouseEvent; regs: Registration[]; onChange: () => void }) {
  const groups = useMemo(() => ({
    attended: regs.filter((r) => r.arrival_status === "checked_in" && !r.is_walk_in),
    walkIns: regs.filter((r) => r.arrival_status === "checked_in" && r.is_walk_in),
    missed: regs.filter((r) => r.arrival_status === "no_show"),
    cancelled: regs.filter((r) => r.arrival_status === "cancelled" || r.registration_status === "cancelled"),
    needsFollowUp: regs.filter((r) => r.follow_up_status === "needed"),
  }), [regs]);

  return (
    <div className="space-y-3">
      <FollowUpSection title="Attended" desc="Send a thank-you and any classes they were curious about." icon={CheckCircle2} rows={groups.attended} onChange={onChange} />
      <FollowUpSection title="Walk-ins" desc="Showed up without an RSVP." icon={UserPlus} rows={groups.walkIns} onChange={onChange} />
      <FollowUpSection title="Registered but didn't attend" desc="Consider a warm 'we missed you' note." icon={XCircle} rows={groups.missed} onChange={onChange} />
      <FollowUpSection title="Needs follow-up" desc="Flagged during or after the event." icon={StickyNote} rows={groups.needsFollowUp} onChange={onChange} />
      {groups.cancelled.length > 0 && (
        <FollowUpSection title="Cancelled" desc="RSVPs that were cancelled." icon={XCircle} rows={groups.cancelled} onChange={onChange} />
      )}
    </div>
  );
}

function FollowUpSection({ title, desc, icon: Icon, rows, onChange }: {
  title: string; desc: string; icon: any; rows: Registration[]; onChange: () => void;
}) {
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" /> {title}
          <Badge variant="outline">{rows.length}</Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </CardHeader>
      <CardContent className="divide-y p-0">
        {rows.map((r) => (
          <div key={r.id} className="px-4 py-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{r.first_name} {r.last_name}</p>
              <p className="text-xs text-muted-foreground truncate">{r.email}</p>
            </div>
            {r.follow_up_status === "needed" && (
              <Button size="sm" variant="ghost" onClick={async () => { await completeFollowUp(r); onChange(); }}>
                Mark done
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/* ───────────────────── Reminders ───────────────────── */

function RemindersTab({ event, onChange }: { event: OpenHouseEvent; onChange: () => void }) {
  const [reminders, setReminders] = useState<any[]>([]);
  const [seeding, setSeeding] = useState(false);
  const load = async () => {
    const { data } = await (supabase as any).from("open_house_reminders").select("*").eq("event_id", event.id).order("scheduled_at");
    setReminders(data ?? []);
  };
  useEffect(() => { load(); }, [event.id]);

  const seed = async () => {
    setSeeding(true);
    const rows = defaultReminderSchedule(event.event_date, event.start_time).map((r) => ({
      event_id: event.id, kind: r.kind, audience: "both", scheduled_at: r.scheduled_at, status: "scheduled",
    }));
    const { error } = await (supabase as any).from("open_house_reminders").upsert(rows, { onConflict: "event_id,kind,audience", ignoreDuplicates: true });
    if (error) toast({ title: "Couldn't schedule reminders", description: error.message, variant: "destructive" });
    else toast({ title: "Reminder schedule ready" });
    setSeeding(false);
    load();
  };

  const cancel = async (id: string) => {
    await (supabase as any).from("open_house_reminders").update({ status: "cancelled" }).eq("id", id);
    load();
  };

  const runNow = async (id: string) => {
    await (supabase as any).from("open_house_reminders").update({ scheduled_at: new Date().toISOString() }).eq("id", id);
    const { data } = await supabase.functions.invoke("dispatch-open-house-reminders", { body: {} });
    load(); onChange();
    toast({ title: "Reminder dispatched", description: `Processed ${(data as any)?.processed ?? 0} due reminder(s).` });
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="font-heading">Reminder schedule</CardTitle>
        <Button size="sm" variant="outline" onClick={seed} disabled={seeding}>
          <Bell className="h-3.5 w-3.5 mr-1" /> Set up default schedule
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {reminders.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No reminders scheduled yet. Click "Set up default schedule" to add 7-day, 2-day, morning-of, and follow-up reminders.
          </p>
        ) : (
          <div className="divide-y">
            {reminders.map((r) => (
              <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{reminderKindLabel(r.kind)}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(r.scheduled_at).toLocaleString()} · {r.audience}
                    {r.status === "sent" && ` · sent to ${r.sent_count}`}
                    {r.status === "failed" && ` · failed`}
                    {r.status === "cancelled" && ` · cancelled`}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  {r.status === "scheduled" && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => runNow(r.id)}><Send className="h-3.5 w-3.5 mr-1" />Send now</Button>
                      <Button size="sm" variant="ghost" onClick={() => cancel(r.id)}>Cancel</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function reminderKindLabel(k: ReminderKind) {
  switch (k) {
    case "confirmation": return "Registration confirmation";
    case "seven_day": return "7 days before";
    case "two_day": return "2 days before";
    case "morning_of": return "Morning of event";
    case "follow_up": return "Day-after follow-up";
    default: return "Ad-hoc reminder";
  }
}

/* ───────────────────── Check-In Mode (mobile-first) ───────────────────── */

function CheckInMode({ event, regs, snapshot, onExit, onChange }:
  { event: OpenHouseEvent; regs: Registration[]; snapshot: Snapshot; onExit: () => void; onChange: () => void }) {
  const [q, setQ] = useState("");
  const active = regs.filter((r) => r.registration_status !== "cancelled" && r.arrival_status !== "cancelled");
  const filtered = active.filter((r) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase().trim();
    return (`${r.first_name} ${r.last_name}`.toLowerCase().includes(needle) ||
      r.email.toLowerCase().includes(needle) ||
      (r.phone ?? "").toLowerCase().includes(needle));
  });
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [detailReg, setDetailReg] = useState<Registration | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-primary font-semibold">Check-in mode</p>
          <h1 className="font-heading text-2xl">{event.title}</h1>
        </div>
        <Button variant="outline" onClick={onExit}>Exit</Button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <BigStat label="Expected" value={snapshot.total_headcount} />
        <BigStat label="Checked in" value={snapshot.checked_in_heads} highlight />
        <BigStat label="Walk-ins" value={snapshot.walk_ins} />
      </div>

      <div className="flex gap-2 sticky top-0 bg-background py-2 z-10">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, or phone" className="pl-9 h-12 text-base" autoFocus />
        </div>
        <Button size="lg" onClick={() => setWalkInOpen(true)} className="gap-1"><Plus className="h-4 w-4" />Walk-in</Button>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && (
          <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
            No matches. Try a different spelling or add a walk-in.
          </CardContent></Card>
        )}
        {filtered.map((r) => (
          <button key={r.id} onClick={() => setDetailReg(r)} className="w-full text-left">
            <Card className={r.arrival_status === "checked_in" ? "border-primary/40 bg-primary/5" : ""}>
              <CardContent className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-base">{r.first_name} {r.last_name}</span>
                    <ArrivalBadge status={r.arrival_status} />
                    {r.is_walk_in && <Badge variant="outline" className="text-[10px]">Walk-in</Badge>}
                    {r.attendee_type === "teacher" && <Badge variant="outline" className="text-[10px]">Instructor</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{r.email}{r.phone ? ` · ${r.phone}` : ""}</p>
                  <p className="text-xs mt-0.5">
                    {r.total_headcount} attendee{r.total_headcount === 1 ? "" : "s"}
                    {r.attendee_type === "parent" && ` · ${r.adult_count}A / ${r.child_count}C`}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
              </CardContent>
            </Card>
          </button>
        ))}
      </div>

      {detailReg && (
        <RegistrationDetailSheet reg={detailReg} onClose={() => setDetailReg(null)} onChange={() => { setDetailReg(null); onChange(); }} />
      )}
      {walkInOpen && (
        <WalkInSheet event={event} onClose={() => setWalkInOpen(false)} onChange={() => { setWalkInOpen(false); onChange(); }} />
      )}
    </div>
  );
}

function BigStat({ label, value, highlight }: { label: string; value: React.ReactNode; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 text-center ${highlight ? "border-primary/40 bg-primary/5" : ""}`}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</p>
      <p className="font-heading text-3xl">{value}</p>
    </div>
  );
}

function RegistrationDetailSheet({ reg, onClose, onChange }: { reg: Registration; onClose: () => void; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [adults, setAdults] = useState(reg.adult_count);
  const [kids, setKids] = useState(reg.child_count);
  const [note, setNote] = useState("");

  const run = async (fn: () => Promise<void>, successMsg: string) => {
    setBusy(true);
    try { await fn(); toast({ title: successMsg }); onChange(); }
    catch (e: any) { toast({ title: "Couldn't save", description: e?.message ?? "Try again.", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading text-xl">{reg.first_name} {reg.last_name}</SheetTitle>
          <p className="text-sm text-muted-foreground">{reg.email}{reg.phone ? ` · ${reg.phone}` : ""}</p>
        </SheetHeader>
        <div className="pt-6 space-y-4">
          {reg.arrival_status !== "checked_in" ? (
            <Button size="lg" className="w-full h-14 text-base" disabled={busy}
              onClick={() => run(() => checkInRegistration(reg), "Checked in")}>
              <CheckCircle2 className="h-5 w-5 mr-2" /> Check in {reg.attendee_type === "parent" ? "family" : "instructor"}
            </Button>
          ) : (
            <Button size="lg" variant="outline" className="w-full h-14 text-base" disabled={busy}
              onClick={() => run(() => resetArrival(reg), "Reset to expected")}>
              <RotateCcw className="h-5 w-5 mr-2" /> Reset arrival
            </Button>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Adults</Label>
              <CountInput value={adults} onChange={setAdults} min={0} max={20} />
            </div>
            <div>
              <Label className="text-xs">Children</Label>
              <CountInput value={kids} onChange={setKids} min={0} max={20} />
            </div>
          </div>
          {(adults !== reg.adult_count || kids !== reg.child_count) && (
            <Button className="w-full" disabled={busy}
              onClick={() => run(() => adjustHeadcount(reg, { adult_count: adults, child_count: kids, additional_guest_count: reg.additional_guest_count }), "Headcount updated")}>
              Save new headcount
            </Button>
          )}

          <div className="space-y-2">
            <Label className="text-xs">Add a note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Interested in Mommy & Me Crochet" />
            <Button variant="outline" className="w-full" disabled={busy || !note.trim()}
              onClick={async () => { await run(() => addNote(reg, note.trim()), "Note saved"); setNote(""); }}>
              <StickyNote className="h-4 w-4 mr-1" /> Save note
            </Button>
          </div>

          {reg.internal_notes && (
            <div className="text-xs whitespace-pre-line text-muted-foreground p-3 bg-muted/40 rounded">{reg.internal_notes}</div>
          )}

          {reg.arrival_status !== "no_show" && (
            <Button variant="outline" className="w-full text-destructive" disabled={busy}
              onClick={() => run(() => markNoShow(reg), "Marked no-show")}>
              <XCircle className="h-4 w-4 mr-1" /> Mark no-show
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function WalkInSheet({ event, onClose, onChange }: { event: OpenHouseEvent; onClose: () => void; onChange: () => void }) {
  const [attendeeType, setAttendeeType] = useState<"parent" | "teacher">("parent");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [adults, setAdults] = useState(1);
  const [kids, setKids] = useState(0);
  const [busy, setBusy] = useState(false);
  const [instructorStatus, setInstructorStatus] = useState<"currently_teaches" | "interested_in_teaching">("interested_in_teaching");

  const submit = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast({ title: "Name and email required", variant: "destructive" }); return;
    }
    setBusy(true);
    try {
      const user = (await supabase.auth.getUser()).data.user;
      const { data: reg, error } = await (supabase as any)
        .from("open_house_registrations")
        .insert({
          event_id: event.id,
          attendee_type: attendeeType,
          first_name: firstName.trim(), last_name: lastName.trim(),
          email: email.trim().toLowerCase(), phone: phone.trim() || null,
          adult_count: attendeeType === "parent" ? adults : 1,
          child_count: attendeeType === "parent" ? kids : 0,
          additional_guest_count: 0,
          is_walk_in: true,
          source: "walk_in",
          arrival_status: "checked_in",
          checked_in_at: new Date().toISOString(),
          checked_in_by: user?.id ?? null,
          instructor_status: attendeeType === "teacher"
            ? (instructorStatus === "currently_teaches" ? "Currently teaching" : "Interested in teaching")
            : null,
        })
        .select("id").single();
      if (error) throw error;
      await (supabase as any).from("open_house_attendance_events").insert({
        event_id: event.id, registration_id: reg.id, kind: "walk_in_added",
        actor_user_id: user?.id ?? null,
      });
      toast({ title: "Walk-in added and checked in" });
      onChange();
    } catch (e: any) {
      toast({ title: "Couldn't add walk-in", description: e?.message ?? "Try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader><SheetTitle className="font-heading text-xl">Add a walk-in</SheetTitle></SheetHeader>
        <div className="pt-6 space-y-4">
          <RadioGroup value={attendeeType} onValueChange={(v) => setAttendeeType(v as any)} className="grid grid-cols-2 gap-2">
            <label className="border rounded-lg p-3 cursor-pointer flex items-center gap-2">
              <RadioGroupItem value="parent" /><Users className="h-4 w-4" /> Family
            </label>
            <label className="border rounded-lg p-3 cursor-pointer flex items-center gap-2">
              <RadioGroupItem value="teacher" /><GraduationCap className="h-4 w-4" /> Instructor
            </label>
          </RadioGroup>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">First name *</Label><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} /></div>
            <div><Label className="text-xs">Last name *</Label><Input value={lastName} onChange={(e) => setLastName(e.target.value)} /></div>
          </div>
          <div><Label className="text-xs">Email *</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <div><Label className="text-xs">Phone</Label><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>

          {attendeeType === "parent" ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Adults</Label><CountInput value={adults} onChange={setAdults} min={1} max={20} /></div>
              <div><Label className="text-xs">Children</Label><CountInput value={kids} onChange={setKids} min={0} max={20} /></div>
            </div>
          ) : (
            <RadioGroup value={instructorStatus} onValueChange={(v) => setInstructorStatus(v as any)}>
              <label className="flex items-center gap-2 py-1 cursor-pointer">
                <RadioGroupItem value="currently_teaches" /> Currently teaching with us
              </label>
              <label className="flex items-center gap-2 py-1 cursor-pointer">
                <RadioGroupItem value="interested_in_teaching" /> Interested in teaching
              </label>
            </RadioGroup>
          )}

          <Button size="lg" className="w-full h-14" disabled={busy} onClick={submit}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Add and check in
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ───────────────────── Small pieces ───────────────────── */

function Stat({ label, value, sub, highlight, accent }: { label: string; value: React.ReactNode; sub?: string; highlight?: boolean; accent?: boolean }) {
  return (
    <Card className={highlight ? "border-primary/40" : accent ? "border-amber-500/30" : ""}>
      <CardContent className="py-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</p>
        <p className="font-heading text-3xl mt-1">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; className: string }> = {
    draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
    registration_open: { label: "Scheduled", className: "bg-primary/15 text-primary border-primary/30" },
    registration_closed: { label: "RSVPs closed", className: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
    completed: { label: "Completed", className: "bg-muted text-muted-foreground" },
    postponed: { label: "Postponed", className: "bg-amber-500/15 text-amber-700 border-amber-500/40" },
    cancelled: { label: "Cancelled", className: "bg-destructive/10 text-destructive" },
    archived: { label: "Archived", className: "bg-muted text-muted-foreground" },
  };
  const s = map[status] ?? { label: status, className: "" };
  return <Badge variant="outline" className={s.className}>{s.label}</Badge>;
}
function ArrivalBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    expected: "bg-muted text-muted-foreground",
    checked_in: "bg-primary/15 text-primary border-primary/30",
    no_show: "bg-destructive/10 text-destructive",
    cancelled: "bg-muted text-muted-foreground line-through",
  };
  const labels: Record<string, string> = { expected: "Expected", checked_in: "Checked in", no_show: "No-show", cancelled: "Cancelled" };
  return <Badge variant="outline" className={`text-[10px] ${map[status] ?? ""}`}>{labels[status] ?? status}</Badge>;
}
function PillButton({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: any; label: string }) {
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-transparent text-muted-foreground border-border hover:text-foreground"}`}>
      <Icon className="h-3.5 w-3.5" />{label}
    </button>
  );
}
function CountInput({ value, onChange, min, max }: { value: number; onChange: (n: number) => void; min: number; max: number }) {
  return (
    <div className="flex items-center gap-2 mt-1">
      <Button type="button" variant="outline" size="icon" className="h-10 w-10" onClick={() => onChange(Math.max(min, value - 1))}>−</Button>
      <div className="w-12 text-center font-medium text-lg">{value}</div>
      <Button type="button" variant="outline" size="icon" className="h-10 w-10" onClick={() => onChange(Math.min(max, value + 1))}>+</Button>
    </div>
  );
}
